import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  InputAdornment,
  Chip,
  SelectChangeEvent,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BusinessIcon from '@mui/icons-material/Business';
import Breadcrumb from 'components/Breadcrumb';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { ApiKey, ApiKeysParams, apiKeysService } from './services/ApiKeysService';
import { logger } from 'utils/logger';
import apiService from 'services/ApiService';
import { environment } from 'environments/environment';

// Import components
import ApiKeysTableHeader, { SortKey } from './components/ApiKeysTableHeader';
import ApiKeyRow from './components/ApiKeyRow';
import ApiKeyRowSkeleton from './components/ApiKeyRowSkeleton';
import TablePagination from './components/TablePagination';
import CreateApiKeyDialog from './components/CreateApiKeyDialog';
import ApiKeyTokenDialog from './components/ApiKeyTokenDialog';

// Import hooks and utilities
import useApiKeysFilter from './hooks/useApiKeysFilter';
import { sortKeyToApiParam, formatDate } from './utils/apiKeyUtils';

const log = logger.getLogger('ApiKeysPage');

const ApiKeysPage: React.FC = () => {
  const theme = useTheme();
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialRowsPerPage = parseInt(searchParams.get('size') || '25', 10);
  const initialSortBy = (searchParams.get('sortBy') || 'id') as SortKey;
  const initialSortDirection = (searchParams.get('sortDir') || 'asc') as 'asc' | 'desc';

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortKey>(initialSortBy);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);

  // Pagination state
  const [page, setPage] = useState<number>(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
  const [paginatedItems, setPaginatedItems] = useState<ApiKey[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [serverPagination] = useState<boolean>(true);

  // Server-side sorting state
  const [serverSortBy, setServerSortBy] = useState<string>(sortKeyToApiParam[initialSortBy] || 'id');
  const [serverSortDirection, setServerSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [newlyCreatedApiKey, setNewlyCreatedApiKey] = useState<ApiKey | null>(null);
  const [apiKeyToToggle, setApiKeyToToggle] = useState<ApiKey | null>(null);

  // Filter hook
  const {
    filterState,
    handleNameFilterChange,
    removeFilter
  } = useApiKeysFilter();

  // Update URL params
  const updateUrlParams = useCallback((newPage: number, newSize: number, newSortBy: SortKey, newSortDir: 'asc' | 'desc') => {
    const params = new URLSearchParams(searchParams);

    if (newPage > 0) {
      params.set('page', newPage.toString());
    } else {
      params.delete('page');
    }

    if (newSize !== 25) {
      params.set('size', newSize.toString());
    } else {
      params.delete('size');
    }

    if (newSortBy !== 'id') {
      params.set('sortBy', newSortBy);
    } else {
      params.delete('sortBy');
    }

    if (newSortDir !== 'asc') {
      params.set('sortDir', newSortDir);
    } else {
      params.delete('sortDir');
    }

    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Fetch API keys
  const fetchApiKeys = useCallback(async (pageNum = 0, pageSize = rowsPerPage) => {
    if (!accountId) return;

    try {
      setLoading(true);
      log.log(`Fetching API keys (page ${pageNum}, size ${pageSize}, sort ${serverSortBy}, direction ${serverSortDirection})...`);

      const params: ApiKeysParams = {
        page: pageNum,
        size: pageSize,
        sort: serverSortBy,
        direction: serverSortDirection,
        filters: {
          accountId: Number(accountId),
          name: filterState.name
        }
      };

      const result = await apiKeysService.getApiKeys(params);

      log.log(`Found ${result.data.length} API keys on page ${pageNum + 1} (total: ${result.count})`);

      setApiKeys(result.data);
      setTotalItems(result?.count || 0);
      setError(null);
    } catch (err) {
      log.error('Error fetching API keys:', err);
      setError('Failed to load API keys. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [accountId, rowsPerPage, serverSortBy, serverSortDirection, filterState.name]);

  // Fetch account name
  const fetchAccountName = useCallback(async () => {
    if (!accountId) return;

    try {
      const response = await apiService.makeRequest(
        'get',
        `${environment.services.account.baseUrl}${environment.services.account.basePath}/accounts/${accountId}`
      );
      setAccountName(response?.name || `Account #${accountId}`);
    } catch (err) {
      log.error('Error fetching account details:', err);
      setAccountName(`Account #${accountId}`);
    }
  }, [accountId]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    if (accountId) {
      apiService.invalidateCacheByPattern('/api-keys');
      fetchApiKeys(page, rowsPerPage);
      fetchAccountName();
    }
  }, [fetchApiKeys, fetchAccountName, accountId, page, rowsPerPage]);

  // Handle sorting
  const handleSort = useCallback((key: SortKey) => {
    const isAsc = sortBy === key && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';

    setSortDirection(newDirection);
    setSortBy(key);

    const apiSortParam = sortKeyToApiParam[key];
    setServerSortBy(apiSortParam);
    setServerSortDirection(newDirection);
    setPage(0);
    updateUrlParams(0, rowsPerPage, key, newDirection);
  }, [sortBy, sortDirection, rowsPerPage, updateUrlParams]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 0) {
      setPage(newPage);
      updateUrlParams(newPage, rowsPerPage, sortBy, sortDirection);
    }
  }, [rowsPerPage, sortBy, sortDirection, updateUrlParams]);

  // Update paginated items
  useEffect(() => {
    if (serverPagination) {
      setPaginatedItems(apiKeys);
    } else {
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setPaginatedItems(apiKeys.slice(startIndex, endIndex));
    }
  }, [apiKeys, page, rowsPerPage, serverPagination]);

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback((event: SelectChangeEvent<number>) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    updateUrlParams(0, newRowsPerPage, sortBy, sortDirection);
  }, [sortBy, sortDirection, updateUrlParams]);

  // Pagination info text
  const getPaginationInfo = useCallback(() => {
    if (apiKeys.length === 0) {
      return '0 items';
    }

    const startItem = serverPagination
      ? page * rowsPerPage + 1
      : Math.min(page * rowsPerPage + 1, totalItems);

    const endItem = serverPagination
      ? Math.min(page * rowsPerPage + apiKeys.length, totalItems)
      : Math.min((page + 1) * rowsPerPage, totalItems);

    return `${startItem} - ${endItem} of ${totalItems} items`;
  }, [apiKeys.length, serverPagination, page, rowsPerPage, totalItems]);

  // Handle toggle status
  const handleToggleStatus = useCallback((apiKey: ApiKey) => {
    setApiKeyToToggle(apiKey);
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!apiKeyToToggle) return;

    try {
      await apiKeysService.updateApiKeyStatus({
        id: apiKeyToToggle.id,
        active: !apiKeyToToggle.active
      });
      setConfirmDialogOpen(false);
      setApiKeyToToggle(null);
      fetchApiKeys(page, rowsPerPage);
    } catch (err) {
      log.error('Error toggling API key status:', err);
    }
  }, [apiKeyToToggle, fetchApiKeys, page, rowsPerPage]);

  const handleCancelToggle = useCallback(() => {
    setConfirmDialogOpen(false);
    setApiKeyToToggle(null);
  }, []);

  // Handle create success
  const handleCreateSuccess = useCallback((createdApiKey: ApiKey) => {
    setCreateDialogOpen(false);
    setNewlyCreatedApiKey(createdApiKey);
    setTokenDialogOpen(true);
  }, []);

  // Handle token dialog close
  const handleTokenDialogClose = useCallback(() => {
    setTokenDialogOpen(false);
    setNewlyCreatedApiKey(null);
    fetchApiKeys(page, rowsPerPage);
  }, [fetchApiKeys, page, rowsPerPage]);

  // Memoize table content
  const tableContent = useMemo(() => {
    if (loading) {
      return Array.from({ length: 5 }).map((_, index) => (
        <ApiKeyRowSkeleton key={`skeleton-${index}`} />
      ));
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ color: 'error.main' }}>
            {error}
          </TableCell>
        </TableRow>
      );
    }

    if (paginatedItems.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center">No API keys found</TableCell>
        </TableRow>
      );
    }

    return paginatedItems.map((apiKey) => (
      <ApiKeyRow
        key={apiKey.id}
        apiKey={apiKey}
        onToggleStatus={handleToggleStatus}
        formatDate={formatDate}
      />
    ));
  }, [paginatedItems, loading, error, handleToggleStatus]);

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      <Breadcrumb
        items={[
          {
            label: 'Accounts',
            path: '/accounts',
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: accountName,
            path: `/accounts/${accountId}/edit`,
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: 'API Keys',
            icon: <VpnKeyIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          }
        ]}
      />

      <Fade in={true} timeout={600}>
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          <Box
            p={2.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                size="small"
                placeholder="Search by name..."
                value={filterState.name}
                onChange={(e) => handleNameFilterChange(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }
                }}
                sx={{
                  minWidth: 220,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px'
                  }
                }}
              />
              {filterState.activeFilters.map((filter, index) => (
                <Chip
                  key={index}
                  label={filter}
                  size="small"
                  onDelete={() => removeFilter(filter)}
                  sx={{
                    borderRadius: '16px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    '&:hover': {
                      opacity: 0.9,
                      transform: 'translateY(-1px)'
                    }
                  }}
                />
              ))}
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<AddIcon />}
              sx={{
                ml: 1.5,
                px: 2,
                py: 1,
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                }
              }}
            >
              Create API Key
            </Button>
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <ApiKeysTableHeader
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSort={handleSort}
              />
              <TableBody
                sx={{
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: alpha(theme.palette.background.default, 0.4),
                  }
                }}
              >
                {tableContent}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            paginatedItems={paginatedItems}
            serverPagination={serverPagination}
            handlePageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            getPaginationInfo={getPaginationInfo}
          />
        </Paper>
      </Fade>

      <CreateApiKeyDialog
        open={createDialogOpen}
        accountId={Number(accountId)}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <ApiKeyTokenDialog
        open={tokenDialogOpen}
        apiKey={newlyCreatedApiKey}
        onClose={handleTokenDialogClose}
      />

      <ConfirmationDialog
        open={confirmDialogOpen}
        title={apiKeyToToggle?.active ? 'Deactivate API Key' : 'Activate API Key'}
        content={
          apiKeyToToggle?.active
            ? `Are you sure you want to deactivate the API key "${apiKeyToToggle?.name}"? It will no longer be usable for authentication.`
            : `Are you sure you want to activate the API key "${apiKeyToToggle?.name}"?`
        }
        confirmButtonText={apiKeyToToggle?.active ? 'Deactivate' : 'Activate'}
        confirmButtonColor={apiKeyToToggle?.active ? 'error' : 'success'}
        severity={apiKeyToToggle?.active ? 'warning' : 'info'}
        onConfirm={handleConfirmToggle}
        onCancel={handleCancelToggle}
      />
    </Container>
  );
};

export default ApiKeysPage;
